import { Component, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { LogError, Metadata, RunView, UserInfo } from '@memberjunction/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { UserService } from '../../../services/user.service';
import { NotificationService } from '../../../services/notification.service';
import { AnnouncementService } from '../../../services/announcement.service';
import { AnnouncementsEntity, AnnouncementUserStatesEntity } from 'mj_generatedentities';
import { AnnouncementViewerGridColumns } from '../../../utils/grid-constants';
import { DataGridColumn, AnnouncementViewState, AnnouncementFilters, AnnouncementGridItem } from '../../../utils/interfaces';
import { Utils } from '../../../utils/utils';

@Component({
  selector: 'acs-announcement-viewer',
  templateUrl: './acs-announcement-viewer.html',
  styleUrls: ['./acs-announcement-viewer.scss']
})
export class AcsAnnouncementViewerComponent implements OnInit {
  private userService = inject(UserService);
  private notificationService = inject(NotificationService);
  private announcementService = inject(AnnouncementService);
  private cdr = inject(ChangeDetectorRef);
  utils = inject(Utils);

  // Grid Configuration  
  announcements: AnnouncementGridItem[] = [];
  columns: DataGridColumn[] = AnnouncementViewerGridColumns;
  isLoading = true;
  currentUser!: UserInfo;

  // Filtering
  filters: AnnouncementFilters = {
    status: 'all',
    priority: 'all'
  };
  selectedStatusFilter = 'all';
  selectedPriority = 'all';

  // Modal for full announcement view
  isAnnouncementModalOpen = false;
  currentAnnouncement: AnnouncementsEntity | null = null;
  
  // Legacy data for backward compatibility
  userStates: Map<string, AnnouncementUserStatesEntity> = new Map();

  async ngOnInit(): Promise<void> {
    this.currentUser = this.userService.getUserInfo();
    this.setupDataGrid();
    await this.loadAnnouncements();
  }

  private setupDataGrid(): void {
    // Update action handlers to use component methods
    this.columns = AnnouncementViewerGridColumns.map(col => {
      if (col.key === 'actions' && col.actions) {
        return {
          ...col,
          actions: col.actions.map(action => ({
            ...action,
            handler: (item: { _announcement: AnnouncementsEntity }) => this.openAnnouncementModal(item._announcement)
          }))
        };
      }
      return col;
    });
  }

  async loadAnnouncements(): Promise<void> {
    this.isLoading = true;
    
    try {
      const rv = new RunView();
      
      // Build filter based on current selections
      let announcementFilter = `Status = 'published' AND (ExpiryDate IS NULL OR ExpiryDate > GETUTCDATE())`;
      
      if (this.selectedPriority !== 'all') {
        announcementFilter += ` AND Priority = '${this.selectedPriority}'`;
      }
      
      // Load announcements and user states in parallel
      const [announcementsResult, userStatesResult] = await rv.RunViews([
        {
          EntityName: 'Announcements',
          ExtraFilter: announcementFilter,
          OrderBy: 'Priority DESC, PublishDate DESC',
          ResultType: 'entity_object'
        },
        {
          EntityName: 'Announcement User States',
          ExtraFilter: `UserID = '${this.currentUser.ID}'`,
          ResultType: 'entity_object'
        }
      ]);

      if (announcementsResult.Success) {
        const announcements = announcementsResult.Results as AnnouncementsEntity[];
        const states = userStatesResult.Success ? userStatesResult.Results as AnnouncementUserStatesEntity[] : [];
        
        // Clear existing userStates map and repopulate
        this.userStates.clear();
        states.forEach((state: AnnouncementUserStatesEntity) => {
          this.userStates.set(state.AnnouncementID, state);
        });
        
        // Transform announcements to AnnouncementViewState format
        this.announcements = announcements.map(announcement => this.transformToViewState(announcement));
        
        // Apply default sorting: Unread first, then by publish date (newest first)
        this.applySorting();
        
        // Apply status filter
        this.applyStatusFilter();
        
      } else {
        LogError('Failed to load announcements:', undefined, announcementsResult.ErrorMessage);
        this.notificationService.error('Loading Error', 'Failed to load announcements');
      }

    } catch (error) {
      LogError('Failed to load announcements:', undefined, error);
      this.notificationService.error('Loading Error', 'Failed to load announcements');
    } finally {
      this.isLoading = false;
    }
  }

  private transformToViewState(announcement: AnnouncementsEntity): AnnouncementGridItem {
    const userState = this.userStates.get(announcement.ID);
    const now = new Date();
    const publishDate = new Date(announcement.PublishDate);
    const isNew = (now.getTime() - publishDate.getTime()) < (24 * 60 * 60 * 1000); // 24 hours
    
    const status = userState?.Status || 'unread';
    const validStatus = (['unread', 'read'].includes(status)) ? 
      status as 'unread' | 'read' : 'none';
    
    // Flatten the structure for the grid
    return {
      // Announcement properties at root level
      ID: announcement.ID,
      Title: announcement.Title,
      Content: announcement.Content,
      Priority: announcement.Priority,
      Status: announcement.Status,
      PublishDate: announcement.PublishDate,
      ExpiryDate: announcement.ExpiryDate ?? undefined,
      RequiresAcknowledgment: announcement.RequiresAcknowledgment,
      CreatedByUserID: announcement.CreatedByUserID,
      
      // Computed properties
      isUnread: !userState || userState.Status === 'unread',
      isNew,
      userAction: validStatus,
      
      // Keep references for actions
      _announcement: announcement,
      _userState: userState || null
    };
  }

  private applySorting(): void {
    // Apply default sorting: Unread first, then by publish date (newest first)
    this.announcements.sort((a, b) => {
      // First level: Unread status (unread items first)
      if (a.isUnread !== b.isUnread) {
        return a.isUnread ? -1 : 1; // Unread (-1) comes before read (1)
      }
      
      // Second level: Publish date (newest first)
      const dateA = new Date(a.PublishDate).getTime();
      const dateB = new Date(b.PublishDate).getTime();
      return dateB - dateA; // Descending order (newest first)
    });
  }

  private applyStatusFilter(): void {
    if (this.selectedStatusFilter === 'all') {
      return; // Show all announcements
    }
    
    this.announcements = this.announcements.filter(item => {
      switch (this.selectedStatusFilter) {
        case 'unread':
          return item.isUnread;
        case 'read':
          return !item.isUnread;
        default:
          return true;
      }
    });
  }

  async markAsRead(announcement: AnnouncementsEntity): Promise<void> {
    const state = await this.getUserState(announcement.ID);
    if (state.Status !== 'read') {
      state.Status = 'read';
      state.ReadAt = new Date();
      await state.Save();
      
      // Notify the announcement service to update counts
      await this.announcementService.markAsRead(announcement.ID);
      
      // Refresh the grid to show updated status
      this.loadAnnouncements();
    }
  }


  private async getUserState(announcementId: string): Promise<AnnouncementUserStatesEntity> {
    let state = this.userStates.get(announcementId);
    if (!state) {
      // Create new state for first-time viewing
      const md = new Metadata();
      state = await md.GetEntityObject<AnnouncementUserStatesEntity>('Announcement User States');
      state.NewRecord(); // Initialize as new record
      state.AnnouncementID = announcementId;
      state.UserID = this.currentUser.ID;
      state.Status = 'unread';
      this.userStates.set(announcementId, state);
    }
    return state;
  }


  getPriorityIcon(priority: string): string {
    const icons: Record<string, string> = {
      'low': '🔵',
      'normal': '🟢', 
      'high': '🟡',
      'urgent': '🔴'
    };
    return icons[priority] || '🟢';
  }

  getPriorityClass(priority: string): string {
    return `announcement-viewer__item--${priority}`;
  }

  getStatusText(announcement: AnnouncementsEntity): string {
    const state = this.userStates.get(announcement.ID);
    if (!state) return 'New';
    
    switch (state.Status) {
      case 'read': return 'Read';
      case 'dismissed': return 'Dismissed';
      case 'acknowledged': return 'Acknowledged';
      default: return 'New';
    }
  }

  isUnread(announcement: AnnouncementsEntity): boolean {
    const state = this.userStates.get(announcement.ID);
    return !state || state.Status === 'unread';
  }

  formatDate(dateInput: string | Date): string {
    return this.utils.formatRelativeDate(dateInput);
  }

  // Modal functionality for full announcement view
  openAnnouncementModal(announcement: AnnouncementsEntity): void {
    this.currentAnnouncement = announcement;
    this.isAnnouncementModalOpen = true;
    
    // Auto-mark as read when opened
    this.markAsRead(announcement);
    
    // Trigger change detection
    this.cdr.detectChanges();
  }

  closeAnnouncementModal(): void {
    this.isAnnouncementModalOpen = false;
    this.currentAnnouncement = null;
    
    // Trigger change detection
    this.cdr.detectChanges();
  }




  // Status filter options
  get statusFilterOptions() {
    return [
      { value: 'all', label: '📋 All Announcements' },
      { value: 'unread', label: '🔔 Unread Only' },
      { value: 'read', label: '📖 Seen Only' }
    ];
  }

  // Priority filter options
  get priorityFilterOptions() {
    return [
      { value: 'all', label: '🎯 All Priorities' },
      { value: 'urgent', label: '🔴 Urgent' },
      { value: 'high', label: '🟡 High' },
      { value: 'normal', label: '🟢 Normal' },
      { value: 'low', label: '🔵 Low' }
    ];
  }
}